import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import GroupCommentsPopout from '../components/pm/GroupCommentsPopout';
import { useAuth } from '../utils/AuthContext';
import { resolveGroupCommentsTab } from '../utils/commentAudience';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import {
  buildGroupCommentKey,
  buildGroupMemberTasks,
  collectGroupCommentContext,
  normalizeGroupName,
  type GroupCommentContext,
} from '../utils/groupComments';
import { subscribeToAnnotationsForProject } from '../utils/pdfAnnotationService';
import type { PdfAnnotation, TaskGroup } from '../types';
import './CommentPopoutPreviewPage.css';

function groupFromKey(
  taskGroups: TaskGroup[],
  timberProjects: Array<{ id?: string; dealId?: string }>,
  projectId: string,
  groupKey: string,
): TaskGroup | undefined {
  const sep = groupKey.indexOf('::');
  const wantedName = sep >= 0 ? groupKey.slice(sep + 2) : '';
  return (
    taskGroups.find(
      (g) => g.projectId === projectId && normalizeGroupName(g.name) === wantedName,
    ) ||
    taskGroups.find((g) => {
      const proj = timberProjects.find((p) => p.id === g.projectId);
      const base = proj?.dealId?.trim() || proj?.id || g.projectId;
      return buildGroupCommentKey(base, g.name) === groupKey;
    })
  );
}

function filesContext(projectId: string, groupKey: string): GroupCommentContext {
  const sep = groupKey.indexOf('::');
  const name = sep >= 0 ? groupKey.slice(sep + 2) : '';
  const displayName = name ? name.charAt(0).toUpperCase() + name.slice(1) : 'Files';
  return {
    groupKey: groupKey || `${projectId}::files`,
    displayName,
    projectIds: [projectId],
    groupIds: [],
    taskIds: [],
    notifyUserIds: [],
    primaryProjectId: projectId,
    primaryGroupId: '',
  };
}

/**
 * Notification preview for a comment mention or file mention.
 * Renders the comments popup without Project Hub.
 */
const CommentPopoutPreviewPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const projectId = (searchParams.get('project') || '').trim();
  const groupKey = (searchParams.get('group') || '').trim();
  const taskCommentId = (searchParams.get('taskComment') || '').trim();
  const discussion = (searchParams.get('discussion') || '').trim();
  const collectionsReady = useCRMCollections([
    'taskGroups',
    'projectTasks',
    'taskComments',
    'projectOnboardings',
  ]);
  const {
    timberProjects,
    taskGroups,
    projectTasks,
    projectOnboardings,
    crmUsers,
    updateProjectTask,
  } = useCRM();
  const [annotations, setAnnotations] = useState<PdfAnnotation[]>([]);

  useEffect(() => {
    if (!projectId) return undefined;
    return subscribeToAnnotationsForProject(projectId, setAnnotations);
  }, [projectId]);

  const groupContext = useMemo(() => {
    if (!projectId) return null;
    const project = timberProjects.find((p) => p.id === projectId);
    if (groupKey) {
      const group = groupFromKey(taskGroups, timberProjects, projectId, groupKey);
      if (group) {
        return collectGroupCommentContext(
          group,
          project,
          timberProjects,
          taskGroups,
          projectTasks,
          projectOnboardings,
        );
      }
      if (!collectionsReady) return null;
      return filesContext(projectId, groupKey);
    }
    if (discussion === 'pdf') return filesContext(projectId, '');
    return null;
  }, [
    collectionsReady,
    discussion,
    groupKey,
    projectId,
    projectOnboardings,
    projectTasks,
    taskGroups,
    timberProjects,
  ]);

  const dealId = timberProjects.find((p) => p.id === projectId)?.dealId;
  const initialTab = resolveGroupCommentsTab({
    discussion,
    groupCommentKey: groupContext?.groupKey || groupKey,
  });
  const memberTasks = useMemo(
    () =>
      groupContext
        ? buildGroupMemberTasks(
            groupContext,
            projectTasks,
            timberProjects,
            taskGroups,
            projectOnboardings,
          )
        : [],
    [groupContext, projectOnboardings, projectTasks, taskGroups, timberProjects],
  );

  return (
    <div className="comment-popout-preview">
      {!projectId || !groupContext ? (
        <p className="comment-popout-preview-status">Loading comments…</p>
      ) : (
        <GroupCommentsPopout
          groupContext={groupContext}
          popoutToken={1}
          dealId={dealId}
          projectPdfAnnotations={annotations}
          highlightCommentId={taskCommentId || null}
          initialTab={initialTab}
          onClose={() => undefined}
          groupMemberTasks={memberTasks}
          teamMembers={crmUsers}
          currentUserId={user?.id}
          onUpdateTask={updateProjectTask}
          architectSurface={user?.role === 'architect'}
        />
      )}
    </div>
  );
};

export default CommentPopoutPreviewPage;
